import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import Gender from "../components/Gender";
import SelectOption from "../components/SelectOption";
import TextInput from "../components/TextInput";
import { API } from "../constants/api";
import { stateRights } from "../config/stateRights";
import {
  RELATION_OPTIONS,
  MAX_LENGTHS,
  RELATION_OPTIONS_MINOR,
} from "../constants/options";
import { useAppDispatch } from "../redux/hooks";
import { ApiCallWithLoader, errorUpdate } from "../redux/reducers/LoaderSlice";
import { updatedPatient } from "../redux/reducers/PatientSlice";
import {
  AddressSchema,
  BasicDetailsSchema,
  EmergencyContactSchema,
} from "../schemas/Patient.schema";
import ToFhir from "../utility/ToFhir";
import Utility from "../utility/Utility";
import "../../src/styles/UpdatePatient.scss";
import moment from "moment";
import Datepicker from "../components/Datepicker";
import UserExistPopUp from "src/components/UserExistPopUp";
import LeydenSelect from "src/components/LeydenSelect";
import { PAGE } from "src/constants/page";
import { useAppSelector } from "../../src/redux/hooks";
import { PersistState } from "../../src/redux/reducers/PersistSlice";
import { UserConfigState } from "../redux/reducers/UserConfigSlice";

const UpdatePatient = () => {
  const [patientUpdated, setPatientUpdated] = useState(false);
  const [patientDetails, setPatientDetails] = useState<any>();

  const [formSchema, setFormSchema] = useState<any>();
  const [practitionerRoles, setPractitionerRoles] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>();
  const [districtPractitionerRoleId, setDistrictPractitionerRoleId] =
    useState<any>(null);
  const [statePractitionerRoleId, setStatePractitionerRoleId] =
    useState<any>(null);

  const [showExistPopUp, setShowExistPopUp] = useState(false);
  const [stateList, setStateList] = useState<any[]>([{ label: "", value: "" }]);
  const [districtList, setDistrictList] = useState<any[]>([
    { label: "", value: "" },
  ]);
  const [cities, setCities] = useState<any>([{ label: "", value: "" }]);
  const [idType, setIdType] = useState<string>("");
  const [idTypeMax, setIdTypeMax] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userState = Utility.getStateOfUser();
  const patientId: string = location.pathname.split("/").pop() || "";
  const stateRight =
    stateRights[
      patientDetails?.address[0]?.state.toLowerCase().replace(/ /g, "")
    ];
  const userConfigDetails: any = useAppSelector(UserConfigState);
  const idOptions =
    Utility.getIdTypesForState(userState) ||
    userConfigDetails?.countryIdentifierOptions;

  const schemaAdult = BasicDetailsSchema.concat(EmergencyContactSchema).concat(
    AddressSchema
  );
  const emerhencyContactSchema = BasicDetailsSchema.concat(
    EmergencyContactSchema
  ).concat(AddressSchema);
  const formRef: any = useRef(null);
  const [validateOnChangeFlag, setvalidateOnChangeFlag] = useState(false);
  const userPersistData = useAppSelector(PersistState);
  const userCountry = userPersistData.country;

  function scrollOnInputSelected() {
    if (
      document?.activeElement?.tagName === "INPUT" ||
      document?.activeElement?.tagName === "TEXTAREA" ||
      document?.activeElement?.tagName === "SELECT"
    ) {
      window.setTimeout(function () {
        (document?.activeElement as any)?.scrollIntoViewIfNeeded();
      }, 0);
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener("resize", scrollOnInputSelected);
    return function cleanupListener() {
      window.removeEventListener("resize", scrollOnInputSelected);
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      const patientRequestDetails = {
        url: API.PATIENT_DETAILS.replace(":id", patientId),
        method: "GET",
      };
      const patientRequestDetailsResponse = await dispatch(
        ApiCallWithLoader(patientRequestDetails)
      );
      setPatientDetails(patientRequestDetailsResponse?.payload);
      const schema = emerhencyContactSchema;
      setFormSchema(schema);
      setStartDate(new Date(patientRequestDetailsResponse.payload.birthDate));
      setIdType(patientDetails?.identifier?.[0]?.assigner?.display || "");
      formik.setFieldValue(
        "state",
        patientRequestDetailsResponse.payload?.address[0]?.state
      );
      formik.setFieldValue(
        "district",
        patientRequestDetailsResponse.payload?.address[0]?.district || ""
      );
      formik.setFieldValue(
        "city",
        patientRequestDetailsResponse.payload?.address[0]?.city || ""
      );
    }

    fetchData();

    if (patientUpdated === true) {
      navigate(PAGE.VIEW_PATIENT.replace(":id", patientId));
    }
    dispatch(updatedPatient(patientUpdated));
  }, [patientUpdated]);

  useEffect(() => {
    async function fetchData() {
      if (
        !stateRight?.edit_patient?.address?.state &&
        !stateRight?.edit_patient?.address?.district &&
        !stateRight?.edit_patient?.address?.city
      ) {
        setStateList([
          {
            label: formik.values.state,
            value: formik.values.state,
          },
        ]);
        setDistrictList([
          {
            label: formik.values.district,
            value: formik.values.district,
          },
        ]);
        setCities([
          {
            label: formik.values.city,
            value: formik.values.city,
          },
        ]);
      } else {
        const stateList: any[] = (await getStates()) || [];
        const districtsList: any[] =
          (await getDistrictsForState(formik?.values?.state)) || [];
        const cityList: any[] = await getCities(
          formik?.values?.state,
          formik?.values?.district
        );
        setStateList([...stateList]);
        setDistrictList([...districtsList]);
        setCities([...cityList]);
      }
    }
    if (stateRight?.edit_patient?.address) {
      fetchData();
    }
  }, [stateRight]);

  const formik = useFormik({
    initialValues: {
      gender: patientDetails ? patientDetails.gender : "",
      firstName: patientDetails ? patientDetails.name[0].given[0] : "",
      middleName: patientDetails && patientDetails.name[0]?.given[1] ? patientDetails.name[0].given[1] : "",
      lastName: patientDetails ? patientDetails.name[0].family : "",
      birthDate: patientDetails ? patientDetails.birthDate : "", // not in use
      idNumber:
        patientDetails && patientDetails?.identifier
          ? Utility.getIDNumber(patientDetails)
          : "",
      mobileNumber:
        patientDetails && patientDetails?.telecom
          ? patientDetails.telecom[0].value
          : "",
      id:
        patientDetails && patientDetails?.identifier
          ? patientDetails?.identifier[0]?.assigner?.display
          : "",
      relationship: patientDetails?.contact
        ? patientDetails.contact[0]?.relationship[0].text
        : "",
      name: patientDetails?.contact
        ? patientDetails?.contact[0]?.name?.given[0]
        : "",
      contactNumber: patientDetails?.contact
        ? patientDetails?.contact[0]?.telecom[0]?.value
        : "",
      state:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0]?.state
          : "",
      district:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0]?.district
          : "",
      street1:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].line[0]
          : "",
      street2:
        patientDetails &&
        patientDetails.address &&
        patientDetails?.address[0].line[1] !== undefined
          ? patientDetails?.address[0].line[1]
          : "",
      city:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].city
          : "",
      pincode:
        patientDetails && patientDetails.address
          ? patientDetails?.address[0].postalCode
          : "",
      selectedOption: patientDetails ? patientDetails.birthDate : "",
    },
    enableReinitialize: true,
    onSubmit: (values: any) => {
      updatePatient();
    },
    validateOnChange: validateOnChangeFlag,
    validationSchema: formSchema,
  });

  async function getPractitionerRoleIds(values: any) {
    try {
      let practitionerRoleIds: string[] = [];
      let locationId = [];

      const globalPractitionerRequestDetails = {
        url:
          API.PRACTITIONER_ROLES_ID + "?role:text=GlobalAdmin PractitionerRole",
        method: "GET",
      };
      const globalPractitionerID = await dispatch(
        ApiCallWithLoader(globalPractitionerRequestDetails)
      );

      if (globalPractitionerID?.payload?.entry[0]?.resource?.id) {
        practitionerRoleIds.push(
          globalPractitionerID?.payload?.entry[0]?.resource?.id
        );
      }

      const getStateLocationRequestDetails = {
        url: API.STATE_COUNT + `&name=${values?.state}`,
        method: "GET",
      };
      const stateLocation = await dispatch(
        ApiCallWithLoader(getStateLocationRequestDetails)
      );

      const getDistrictLocationRequestDetails = {
        url:
          API.DISTRICT_COUNT +
          `&name=${values?.district}&address:state=${values?.state}`,
        method: "GET",
      };
      const districtLocation = await dispatch(
        ApiCallWithLoader(getDistrictLocationRequestDetails)
      );

      const getCityLocationRequestDetails = {
        url:
          API.CITY_VILLAGE_BY_DISTRICTS.replace(
            ":state",
            values?.state?.toLowerCase()
          ).replace(":district", values?.district?.toLowerCase()) +
          `&name=${values?.city}`,

        method: "GET",
      };

      const cityLocation = await dispatch(
        ApiCallWithLoader(getCityLocationRequestDetails)
      );

      locationId.push(stateLocation?.payload?.entry[0]?.resource?.id);
      locationId.push(districtLocation?.payload?.entry[0]?.resource?.id);
      locationId.push(cityLocation?.payload?.entry[0]?.resource?.id);

      for (let i = 0; i < locationId.length; i++) {
        let locationID = `?location=LOCATION/${locationId[i]}`;
        const getPractitionerDetails = {
          url: API.PRACTITIONER_ROLES_ID + locationID,
          method: "GET",
        };
        const practitionerId = await dispatch(
          ApiCallWithLoader(getPractitionerDetails)
        );
        practitionerRoleIds.push(
          practitionerId?.payload?.entry[0]?.resource?.id
        );
      }
      return practitionerRoleIds || [];
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  const updatePatient = async () => {
    try {
      const searchQuery = Utility.uniqueListToQueryString(
        stateRight.uniquenessCheckList,
        formik.values
      );
      const searchUrl = API.SEARCH + searchQuery;
      const requestDetailsGet = {
        url: searchUrl,
        method: "GET",
      };
      const searchResult = await dispatch(ApiCallWithLoader(requestDetailsGet));
      const userEntry = searchResult?.payload?.entry;

      if (userEntry) {
        for (let i = 0; i < userEntry.length; i++) {
          if (userEntry[i]?.resource?.id !== patientId) {
            setShowExistPopUp(true);
            return null;
          }
        }
      }

      let generalPractitionerDetails;
      generalPractitionerDetails = patientDetails.generalPractitioner;
      /*if (!stateRight?.address?.district && !stateRight?.address?.city) {
        generalPractitionerDetails = patientDetails.generalPractitioner;
      } else {
        generalPractitionerDetails = ToFhir.generalPractitioner(
          (await getPractitionerRoleIds(formik.values)) || []
        );
      }*/
      const postData = {
        resourceType: "Patient",
        id: patientId,
        name: [
          ToFhir.patientName(formik.values.firstName, formik.values.middleName, formik.values.lastName),
        ],
        gender: formik.values.gender,
        birthDate: formik.values.birthDate,
        address: ToFhir.address({
          state: formik.values.state,
          district: formik.values.district,
          street1: formik.values.street1,
          street2: formik.values.street2,
          city: formik.values.city,
          pincode: formik.values.pincode,
        }),
        generalPractitioner: generalPractitionerDetails,
        telecom: ToFhir.telecom(parseInt(formik.values.mobileNumber)),
        ...(formik.values?.relationship !== "" && {
          contact: ToFhir.contact({
            relationship: formik.values.relationship,
            name: getEmergencyContactName(),
            contactNumber: formik.values.contactNumber,
          }),
        }),
        ...(formik.values?.id &&
          formik.values?.idNumber !== "" && {
            identifier: ToFhir.identifier(
              formik.values.id,
              formik.values.idNumber.toLowerCase()
            ),
          }),
      };

      const requestDetails = {
        url: API.PATIENT_DETAILS.replace(":id", patientId),
        method: "PUT",
        requestData: postData,
      };

      const resp: any = await dispatch(ApiCallWithLoader(requestDetails));
      if (resp.payload) {
        setPatientUpdated(true);
      }
      return resp;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const handleCancel = () => {
    navigate(PAGE.VIEW_PATIENT.replace(":id", patientId));
  };

  const handleStateChange = async (option: any) => {
    try {
      const districtList = await getDistrictsForState(option.target.value);
      formik.setFieldValue("state", option.target.value);
      setDistrictList([...districtList]);
      formik.setFieldValue("district", districtList[0].value);
      const citiesList = await getCities(
        option.target.value,
        districtList[0].value
      );
      setCities([...citiesList]);
      formik.setFieldValue("city", citiesList[0].value);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  async function handleDistrictChange(option: any) {
    try {
      formik.setFieldValue("district", option.target.value);
      const citiesList = await getCities(
        formik.values.state,
        option.target.value
      );
      setCities([...citiesList]);
      formik.setFieldValue("city", citiesList[0].value);
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  function selectCity(option: any) {
    // Set the form value
    formik.setFieldValue("city", option.target.value);
  }

  const handleGenderChange = (e: any) => {
    if (e.target.value) {
      formik.setFieldValue("gender", e.target.value);
    }
  };

  const handleDate = (e: any) => {
    setStartDate(e);
    formik.setFieldValue(
      "birthDate",
      moment(e).format("YYYY-MM-DD") === "Invalid date"
        ? ""
        : moment(e).format("YYYY-MM-DD")
    );
    formik.setFieldValue("age", "");
    const schema = emerhencyContactSchema;
    setFormSchema(schema);
  };

  const handleGoback = () => {
    navigate(PAGE.VIEW_PATIENT_DETAILS.replace(":id", patientId));
  };

  async function getStates() {
    try {
      const requestDetails = {
        url: API.STATES,
        method: "GET",
      };
      const StateLocationList = await dispatch(
        ApiCallWithLoader(requestDetails)
      );
      const stateLocationToSelectMap: any[] =
        StateLocationList.payload.entry.map((data: any) => {
          return { label: data?.resource?.name, value: data?.resource?.name };
        });
      return stateLocationToSelectMap;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  async function getDistrictsForState(stateName: string) {
    try {
      const requestDetails = {
        url: API.DISTRICTS_BY_STATE.replace(":stateName", stateName),
        method: "GET",
      };
      const districtLocation = await dispatch(
        ApiCallWithLoader(requestDetails)
      );
      const districtLocationList: any = districtLocation.payload.entry.map(
        (data: any) => {
          return { label: data?.resource?.name, value: data?.resource?.name };
        }
      );
      return districtLocationList;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }

  async function getCities(stateName: string, districtName: string) {
    try {
      const getCitiesCountUrl = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const countRequestDetails = {
        url: `${getCitiesCountUrl}&_count=1`,
        method: "GET",
      };
      const getCityDataCount = await dispatch(
        ApiCallWithLoader(countRequestDetails)
      );
      const total_count = getCityDataCount?.payload?.total;
      const roleRequest_url = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const requestDetails = {
        url: `${roleRequest_url}&_count=${total_count}`,
        method: "GET",
      };
      const getCityData = await dispatch(ApiCallWithLoader(requestDetails));
      const cities = getCityData?.payload?.entry.map((data: any) => {
        return { label: data.resource.name, value: data.resource.name };
      });
      return cities;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  }
  //load edit rights json file based on state

  const beneficiaryIdDetails = (e: any) => {
    if (e.target.value) {
      formik.setFieldValue("idNumber", e.target.value);
    } else {
      formik.setFieldValue("idNumber", "");
    }
  };

  const handleSelectID = (option: any) => {
    formik.setFieldValue("idNumber", "");
    formik.setFieldValue("id", option.value);
    setIdTypeMax(MAX_LENGTHS[option.value] || 12);
    setIdType(option.value);
    setFormSchema(emerhencyContactSchema);
  };

  const handleSelectContact = (option: any) => {
    formik.setFieldValue("relationship", option.value);
    formRef.current.reset();
    formik.setFieldValue(
      "name",
      `${
        option.value === "self"
          ? formik?.values?.firstName + " " + formik?.values?.lastName
          : ""
      }`
    );
    formik.setFieldValue(
      "contactNumber",
      `${option.value === "self" ? formik.values.mobileNumber : ""}`
    );
  };

  const getEmergencyContactName = () => {
    let contact_name = "";
    if (formik.values.relationship === "self") {
      let fName = formik.values.firstName;
      let mName = formik.values?.middleName;
      let lName = formik.values.lastName;
      let pName = fName + ((mName) ? " " + mName : "") + " " + lName;
      contact_name = pName;
    } else {
      contact_name = formik.values.name;
    }
    return contact_name;
  };

  const getEmergencyContactNumber = () => {
    let contact_number = "";
    if (formik.values.relationship === "self") {
      contact_number = formik.values.mobileNumber;
    } else {
      contact_number = formik.values.contactNumber;
    }
    return contact_number;
  };

  const handleContactNumberBlur = () => {
    let contact_number = "";
    if (formik.values.relationship === "self") {
      contact_number = formik.values?.mobileNumber;
    } else {
      contact_number = formik.values?.contactNumber;
    }
    formik.setFieldValue("contactNumber", contact_number);
  };

  const handleContactNameBlur = () => {
    let contact_name = "";
    if (formik.values.relationship === "self") {
      contact_name = formik.values?.firstName + " " + formik.values?.lastName;
    } else {
      contact_name = formik.values?.name;
    }
    formik.setFieldValue("name", contact_name);
  };

  const emergencyContactEditable = formik.values.relationship === "self";

  if (!patientDetails) {
    return <></>;
  }

  return (
    <>
      <Breadcrumbs
        active_key="edit_profile"
        navigation={navigate}
        location={location.pathname}
        routes={[
          {
            label_key: Utility.patientName(patientDetails?.name),
            fn: handleGoback,
          },
        ]}
      />
      {!patientUpdated && (
        <form
          className="container edit-profile-form"
          onSubmit={(e: any) => {
            if (Utility.isDdMmYyyyEmpty()) {
              e.preventDefault();
              formik.setErrors({ birthDate: "required_birthdate" });
            } else {
              setvalidateOnChangeFlag(true);
              formik.handleSubmit(e);
            }
          }}
          ref={formRef}
        >
          <h3 className="heading-bold mb-3">
            <Trans i18nKey={"edit_profile"}></Trans>
          </h3>
          <div className="bg-light-gray">
            <h5 className="bg-light-blue p-2 heading">
              <Trans i18nKey={"basic_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              {patientDetails && (
                <Gender
                  onChange={handleGenderChange}
                  error={formik.errors.gender}
                  defaultSelected={patientDetails?.gender}
                  disabled={stateRight && stateRight.edit_patient.gender}
                />
              )}

              <div className="mt-4">
                <TextInput
                  label="First Name"
                  label_key="first_name"
                  type="text"
                  id="firstName"
                  maxLength={50}
                  defaultValue={formik.values.firstName}
                  value={formik.values.firstName}
                  error={formik.errors.firstName}
                  onChange={formik.handleChange}
                  disabled={stateRight && stateRight.edit_patient.firstname}
                />
              </div>
              <div className="mt-4">
                <TextInput
                  label="Middle Name"
                  label_key="middle_name"
                  type="text"
                  id="middleName"
                  maxLength={50}
                  defaultValue={formik.values.middleName}
                  value={formik.values.middleName}
                  error={formik.errors.middleName}
                  onChange={formik.handleChange}
                  disabled={stateRight && stateRight.edit_patient.middlename}
                />
              </div>
              <div className="mt-4" onChange={scrollOnInputSelected}>
                <TextInput
                  label="Last Name"
                  label_key="last_name"
                  type="text"
                  id="lastName"
                  maxLength={50}
                  defaultValue={formik.values.lastName}
                  value={formik.values.lastName}
                  error={formik.errors.lastName}
                  onChange={formik.handleChange}
                  onBlur={handleContactNameBlur}
                  disabled={stateRight && stateRight.edit_patient.lastname}
                  mandate={true}
                />
              </div>

              <div className="mt-1">
                <div className="row mt-4">
                  {stateRight && stateRight.edit_patient.dob ? (
                    <Datepicker
                      value={startDate}
                      label_key="birth_date"
                      onChange={handleDate}
                      error={formik.errors.birthDate}
                      testId={"dp-updatepatient"}
                      mandate={true}
                    />
                  ) : (
                    <>
                      <label className="label-1">
                        <Trans i18nKey="birth_date"></Trans>
                      </label>
                      <span className="p-label">
                        {startDate && Utility.formatDate(startDate)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <LeydenSelect
                  label={"Select ID type"}
                  label_key="select_id_optional"
                  options={Utility.getValidIdentifierOptions(
                    idOptions,
                    userCountry
                  )}
                  onChange={handleSelectID}
                  id={"id"}
                  defaultSelected={formik.values.id}
                  error={formik.errors.id}
                  blankOptionLabel={Utility.getTranslatedBlankOption(
                    "select_id_optional"
                  )}
                  disabled={stateRight && stateRight.edit_patient.voter_rch_id}
                />
              </div>
              {formik.values.id && (
                <div className="mt-4">
                  <TextInput
                    label="ID Number"
                    label_key="id_number"
                    type="idNumber"
                    id="idInUpdatePatient"
                    maxLength={MAX_LENGTHS[formik.values.id]}
                    defaultValue={formik.values.idNumber}
                    value={formik.values.idNumber}
                    error={formik.errors.idNumber}
                    onChange={beneficiaryIdDetails}
                    disabled={stateRight && stateRight.edit_patient.idnumber}
                    dynamicPlaceHolder={Utility.getPlaceholderForState(
                      userState,
                      userCountry
                    )}
                  />
                </div>
              )}

              <div className="mt-4">
                <TextInput
                  label="Mobile Number"
                  label_key="mobile_number"
                  type="number"
                  id="mobileNumber"
                  maxLength={10}
                  defaultValue={formik.values.mobileNumber}
                  value={formik.values.mobileNumber}
                  error={formik.errors.mobileNumber}
                  onChange={formik.handleChange}
                  onBlur={handleContactNumberBlur}
                  disabled={stateRight && stateRight.edit_patient.mobile}
                  mandate={true}
                />
              </div>
            </div>
          </div>
          <div className="form-emergency-contact bg-light-gray mt-5">
            <h5 className="bg-light-blue p-2 heading">
              <Trans i18nKey={"emergency_contact_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              <div>
                <LeydenSelect
                  label={"Select Relationship"}
                  label_key="select_relation"
                  options={Utility.getOptions(
                    Utility.isMinor(patientDetails?.birthDate)
                      ? RELATION_OPTIONS_MINOR
                      : RELATION_OPTIONS
                  )}
                  onChange={handleSelectContact}
                  id={"relationship"}
                  defaultSelected={
                    Utility.isMinor(patientDetails?.birthDate) &&
                    formik.values.relationship === "self"
                      ? ""
                      : formik.values.relationship
                  }
                  error={formik.errors.relationship}
                  mandate={true}
                  disabled={
                    stateRight && stateRight.edit_patient.emergencycontact.relationship
                  }
                  blankOptionLabel={Utility.getTranslatedBlankOption(
                    "select_relation"
                  )}
                />
              </div>
              <div className="mt-4">
                <div>
                  <TextInput
                    label="Name"
                    label_key="relative_name"
                    type="text"
                    id="name"
                    maxLength={50}
                    defaultValue={getEmergencyContactName()}
                    disabled={!emergencyContactEditable}
                    mandate={true}
                    error={formik.errors.name}
                    onChange={formik.handleChange}
                    relation={formik?.values?.relationship}
                  />
                  <div className="mt-4">
                    <TextInput
                      label="Contact Number"
                      label_key="contact_number"
                      type="number"
                      id="contactNumber"
                      maxLength={10}
                      defaultValue={getEmergencyContactNumber()}
                      disabled={!emergencyContactEditable}
                      mandate={true}
                      error={formik.errors.contactNumber}
                      onChange={formik.handleChange}
                      relation={formik?.values?.relationship}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="form-address bg-light-gray mt-5">
            <h5 className="bg-light-blue p-2 heading">
              <Trans i18nKey={"address_details"}></Trans>
            </h5>
            <div className="p-3 form-width">
              <div className="mt-1">
                <TextInput
                  label="Street 1"
                  label_key="street1"
                  type="text"
                  id="street1"
                  defaultValue={formik.values.street1}
                  value={formik.values.street1}
                  error={formik.errors.street1}
                  onChange={formik.handleChange}
                  disabled={stateRight && stateRight.edit_patient.address.street1}
                  mandate={true}
                />
              </div>
              <div className="mt-4">
                <TextInput
                  label="Street 2"
                  label_key="street2"
                  type="text"
                  id="street2"
                  defaultValue={formik.values.street2}
                  value={formik.values.street2}
                  error={formik.errors.street2}
                  onChange={formik.handleChange}
                  disabled={stateRight && stateRight.edit_patient.address.street2}
                />
              </div>
              <div className="mt-4">
                <SelectOption
                  label={"City/Town/Village"}
                  label_key="city_village"
                  options={cities}
                  onChange={selectCity}
                  id={"city"}
                  disabled={stateRight?.edit_patient?.address?.city}
                  defaultSelected={formik?.values?.city || ""}
                />
              </div>
              <div className="mt-2">
                <SelectOption
                  label={"District"}
                  label_key="select_district"
                  options={districtList}
                  onChange={handleDistrictChange}
                  id={"district"}
                  disabled={stateRight?.edit_patient?.address?.district}
                  defaultSelected={formik.values.district || ""}
                />
              </div>
              <div className="mt-2">
                <div className="selectOption">
                  <label className="label-1">
                    <Trans i18nKey={"state"}></Trans>{" "}
                    <span className="mandetory">*</span>
                  </label>
                </div>
                <div className="disableAddress">{formik?.values?.state}</div>
              </div>
              <div className="mt-2">
                <TextInput
                  label="Pincode"
                  label_key="pincode"
                  type="number"
                  id="pincode"
                  maxLength={6}
                  defaultValue={formik.values.pincode}
                  value={formik.values.pincode}
                  error={formik.errors.pincode || ""}
                  onChange={formik.handleChange}
                  disabled={stateRight?.edit_patient?.address?.pincode}
                  mandate={true}
                />
              </div>
            </div>
          </div>
          <div className=" btn-container mt-3 updatePatient">
            <div className="text-lg-start text-md-end text-end">
              <Button
                type="button"
                className={"btn-outline cancelButton"}
                onClick={handleCancel}
              >
                <Trans i18nKey={"cancel"}></Trans>
              </Button>
              <Button
                data-testid="contactDetailsSubmitBtn"
                type="submit"
                className={"ms-2"}
              >
                <Trans i18nKey={"save"}></Trans>
              </Button>
            </div>
          </div>
        </form>
      )}
      <UserExistPopUp
        show={showExistPopUp}
        handelClose={() => {
          setShowExistPopUp(false);
        }}
        title_key={"duplicate_record"}
        body_key="duplicate_record_message"
        secondaryButtonTitle_key="close"
      />
    </>
  );
};

export default UpdatePatient;
